import { useMemo } from "react"

import { string } from "prop-types"

import { useDateFormatter, useTranslation } from "@l2r-front/l2r-i18n"
import { FileIcon } from "@l2r-front/l2r-icons"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { MoreMenu, useIsMobileDevice } from "@l2r-front/l2r-ui"

import { I18N_NAMESPACE } from "../../constants/i18n"
import { ModulesInfo } from "../../constants/modules"

import * as Styled from "./DocumentListItem.styled"

export const DocumentListItem = (props) => {
    const {
        document,
        documentTypes,
        onClick,
        menuConfig,
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)
    const dateFormatter = useDateFormatter()
    const isMobile = useIsMobileDevice()

    const hasThumbnail = useMemo(() => {
        return document?.thumbnail && !document?.mime_type?.includes("tiff")
    }, [document])

    const thumbnail = useMemo(() => {
        if (hasThumbnail) {
            return <Styled.Thumbnail src={document.thumbnail} alt={document.filename} />
        } else {
            const fileParts = document.file ? document.filename.split(".") : []
            const extension = fileParts.length >= 2 ? fileParts.pop() : ""
            return <FileIcon extension={extension} />
        }
    }, [document, hasThumbnail])

    const documentTypeIcon = useMemo(() => {
        const type = documentTypes.find(type => type.name === document.document_type)
        return type?.icon ? <Styled.TypeIcon src={type.icon} alt={type.name} /> : null
    }, [document.document_type, documentTypes])

    const AttachmentIcon = useMemo(() => {
        return ModulesInfo[document.attached_object_content_type ?? document.linear_location_type.toLowerCase()]?.icon
    }, [document])

    return (
        <Styled.ListItem>
            <Styled.Card elevation={0} isMobile={isMobile} onClick={() => onClick(document)}>
                <Styled.ThumbnailContainer hasThumbnail={hasThumbnail}>
                    {thumbnail}
                </Styled.ThumbnailContainer>
                <Styled.Content>
                    <Styled.FileContent>
                        <Styled.FileName variant="Regular">
                            {document.filename}
                        </Styled.FileName>
                        <Styled.FileInfoContainer>
                            <Styled.FileInfo>
                                <Styled.DateIcon />
                                <Styled.FileInfoText variant="Small">
                                    {dateFormatter(new Date(document.date), "dd MMM yyyy")}
                                </Styled.FileInfoText>
                            </Styled.FileInfo>
                            <Styled.PointSeparator>•</Styled.PointSeparator>
                            <Styled.FileInfo>
                                <Styled.PersonIcon />
                                <Styled.FileInfoText variant="Small">
                                    {document.created_by}
                                </Styled.FileInfoText>
                            </Styled.FileInfo>
                        </Styled.FileInfoContainer>
                    </Styled.FileContent>
                    <Styled.ChipsContainer>
                        <Styled.Chip
                            label={isMobile ? null : t(I18N_NAMESPACE, ModulesInfo[document.attached_object_content_type ?? document.linear_location_type.toLowerCase()]?.labelKey)}
                            variant="outlined"
                            icon={AttachmentIcon && <AttachmentIcon />}
                            iconOnly={isMobile}
                            size="small"
                        />
                        <Styled.Chip
                            label={document.document_type}
                            variant="outlined"
                            icon={documentTypeIcon}
                            size="small"
                        />
                    </Styled.ChipsContainer>
                </Styled.Content>
                <MoreMenu
                    menuConfig={menuConfig}
                    color="tertiary"
                />
            </Styled.Card>
        </Styled.ListItem>
    )
}

DocumentListItem.propTypes = {
    document: PropTypes.document.isRequired,
    documentTypes: PropTypes.arrayOf(PropTypes.shape({
        name: string.isRequired,
        icon: string,
    })).isRequired,
    parentIcon: PropTypes.node,
    onClick: PropTypes.func,
    menuConfig: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string.isRequired,
        icon: PropTypes.node,
        props: PropTypes.object,
    })),
}